import React, { createContext, useContext, useState } from "react";
import axiosInstance, { handleApiError } from "../services/axiosService";
import {
  getTreeDataForAppUserFields,
  // getTreeDataForCommunityFields,
  getTreeDataForRdbmsFields,
  getTreeDataForTableFields
} from "../utils/treeData/treeDataUtils";

const TableContextApi = createContext();

const useTableContext = () => useContext(TableContextApi);

const TableContext = ({ children }) => {
  const [tableData, setTableData] = useState(null);
  const [rdbmsData, setRdbmsData] = useState(null);
  const [appUserData, setAppUserData] = useState(null);

  function sortByActivityFirst(array) {
    const copyArray = JSON.parse(JSON.stringify(array));
    copyArray.sort((a, b) => {
      if (a.type === "activity" && b.type !== "activity") {
        return -1;
      } else if (a.type !== "activity" && b.type === "activity") {
        return 1;
      } else if (a.type === "activity" && b.type === "activity") {
        if (a.activity_type === "init" && b.activity_type !== "init") {
          return -1;
        } else if (a.activity_type !== "init" && b.activity_type === "init") {
          return 1;
        }
      }
      return 0;
    });
    return copyArray;
  }
  const getTableTypeData = async (type, id) => {
    switch (type) {
      case "rdbms":
        return getSingleRdbmsData(id);
      case "postgres":
        return getSinglePostgresTemplateData(id);
      case "appuser":
        return getSingleAppuserData(id);
      // case "communityuser":
      //   return getSingleCommunityUser(id);
      case "RvTemplate":
        return await getSingleRvTemplateData(id);
      default:
        return getSingleTableData(id);
    }
  };
  const getAllTableData = async ({ signal } = {}) => {
    try {
      const res = await axiosInstance.get("/tables", { signal });

      const tableData = res.data;

      setTableData(tableData);

      return tableData;
    } catch (error) {
      if (error?.name === "AbortError") return; // ignore abort silently
      handleApiError(error);
      throw error; // so useQuery can handle it
    }
  };

  //beacause when creating entity and sm first time that table is not present in tableData,
  //and table not found error comes
  const getOneTableData = async (id) => {
    try {
      const { data } = await axiosInstance.get(`/tables/${id}`);
      setTableData([...tableData, data]);
    } catch (error) {
      handleApiError(error);
    }
  };
  const getAllRdbmsData = async ({ signal } = {}) => {
    try {
      const res = await axiosInstance.get("/rdbms", { signal });

      const rdbmsData = res.data;

      setRdbmsData(rdbmsData);

      return rdbmsData;
    } catch (error) {
      if (error?.name === "AbortError") return; // silent abort
      handleApiError(error);
      throw error; // required so useQuery catches it
    }
  };

  const getAllAppuserData = async () => {
    try {
      const { data } = await axiosInstance.get(`/userConfig`);
      setAppUserData(data);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const getSingleRvTemplateData = async (id) => {
    if (!id) {
      throw new Error("Please provide valid id");
    }

    const { data } = await axiosInstance.get(`/recordview-template/${id}`);
    const templateData = data.fields || [];
    const transformedFields = transformFields(templateData);
    const template = getTreeDataForRdbmsFields(transformedFields);

    return {
      data,
      tableObject: template.tableObject,
      treeData: template.treeData,
      fieldIDObject: template.fieldIDObject
    };
  };
  const getSingleTableData = (id, currentTableData = tableData) => {
    if (!currentTableData || !id) {
      throw new Error("Invalid table");
    }
    const data = currentTableData.find((table) => table.table.id === id);
    if (!data) {
      throw new Error("Table not found");
    }

    const tableTreeData = getTreeDataForTableFields(sortByActivityFirst(data.table.columns_new));

    return {
      data,
      tableObject: tableTreeData.tableObject,
      treeData: tableTreeData.treeData,
      fieldIDObject: tableTreeData.fieldIDObject
    };
  };
  //transform fields of RDBMS in desired format
  const transformFields = (fields) => {
    return fields.map((field) => {
      const { name, type, ...rest } = field;
      const transformedField = {
        id: name,
        display: name,
        field_type: type,
        type: "field",
        activity_label: "",
        data_type: type === "recordset-list" ? "object[]" : "",
        ...rest
      };

      if (type === "recordset-list" && Array.isArray(field.sub_fields)) {
        transformedField.sub_fields = transformFields(field.sub_fields);
      } else {
        transformedField.sub_fields = [];
      }

      return transformedField;
    });
  };

  //transform fields of APPUSER in desired format
  const appUserTransformFields = () => {
    const systemFields = tableData[0].table.columns_new.find((act) => act.activity_id === "system_fields").items;
    const loggedInUser = systemFields.find((extra) => extra.id === "logged_in_user").sub_fields;

    return [...loggedInUser];
  };
  //transform fields of COMMUNITYUSER in desired format
  // const communityUserTransformFields = () => {
  //   const systemFields = tableData[0].table.columns_new.find((act) => act.activity_id === "system_fields").items;
  //   const loggedInUser = systemFields.find((extra) => extra.id === "community_in_user").sub_fields;

  //   return [...loggedInUser];
  // };

  const getSingleRdbmsData = (id, currentTableData = rdbmsData) => {
    if (!currentTableData || !id) {
      throw new Error("Invalid rdbms table");
    }
    const data = currentTableData.find((table) => table.id === id);

    if (!data) {
      throw new Error("Rdbms table not found");
    }
    const transformedFields = transformFields(data.fields);
    const rdbmsTreeData = getTreeDataForRdbmsFields(transformedFields);

    return {
      data,
      tableObject: rdbmsTreeData.tableObject,
      treeData: rdbmsTreeData.treeData,
      fieldIDObject: rdbmsTreeData.fieldIDObject
    };
  };

  const getSinglePostgresTemplateData = async (id) => {
    if (!id) {
      throw new Error("Please provide valid id");
    }

    const { data } = await axiosInstance.get(`/postgres/${id}`);
    const templateData = data.fields || [];
    const transformedFields = transformFields(templateData);
    const template = getTreeDataForRdbmsFields(transformedFields);

    return {
      data,
      tableObject: template.tableObject,
      treeData: template.treeData,
      fieldIDObject: template.fieldIDObject
    };
  };

  const getSingleAppuserData = (currentTableData = appUserData) => {
    if (!currentTableData) {
      throw new Error("Invalid appuser");
    }
    // const data = currentTableData.find((table) => table.id === id);
    const data = currentTableData;
    if (!data) {
      throw new Error("Appuser not found");
    }
    const transformedFields = appUserTransformFields(data.fields || []);
    const appUserTreeData = getTreeDataForAppUserFields(transformedFields);
    return { data, tableObject: appUserTreeData.tableObject, treeData: appUserTreeData.treeData };
  };
  // const getSingleCommunityUser = (currentTableData = appUserData) => {
  //   if (!currentTableData) {
  //     throw new Error("Invalid communityuser");
  //   }
  //   // const data = currentTableData.find((table) => table.id === id);
  //   const data = currentTableData;
  //   if (!data) {
  //     throw new Error("Communityuser not found");
  //   }
  //   const transformedFields = communityUserTransformFields(data.fields || []);
  //   const appUserTreeData = getTreeDataForCommunityFields(transformedFields);
  //   return { data, tableObject: appUserTreeData.tableObject, treeData: appUserTreeData.treeData };
  // };

  return (
    <TableContextApi.Provider
      value={{
        tableData,
        rdbmsData,
        appUserData,
        getAllTableData,
        getOneTableData,
        getAllRdbmsData,
        getAllAppuserData,
        getTableTypeData,
        getSingleRdbmsData,
        getSingleTableData,
        getSingleAppuserData
        // getSingleCommunityUser
      }}
    >
      {children}
    </TableContextApi.Provider>
  );
};

export default TableContext;
export { useTableContext };
